/* =========================================================================
   ClueVine — puzzle links
   Every puzzle is reproducible from a tiny reference: a daily is mode + day
   number, an endless puzzle is mode + difficulty + salt. Encoded into the URL
   hash (#play=cipher.d270 or #play=cipher.hard.K3J9X2A) so a shared result can
   open the exact same board on any static host — even offline, since nothing
   is fetched. An optional score to beat rides along for friendly challenges.
   ========================================================================= */

export type PuzzleRef =
  | { modeId: string; kind: 'daily'; day: number }
  | { modeId: string; kind: 'endless'; difficultyId: string; salt: string };

export interface ChallengeLink {
  ref: PuzzleRef;
  /** The sender's score, when they shared a result. */
  beat?: number;
  /** The sender's display name (length-capped, plain text). */
  by?: string;
}

const ID = /^[a-z][a-z0-9-]{0,23}$/;
const SALT = /^[A-Za-z0-9]{1,16}$/;
const DAY = /^d(\d{1,5})$/;
export const MAX_NAME = 24;

export function formatPuzzleRef(ref: PuzzleRef): string {
  return ref.kind === 'daily'
    ? `${ref.modeId}.d${ref.day}`
    : `${ref.modeId}.${ref.difficultyId}.${ref.salt}`;
}

export function parsePuzzleRef(raw: string): PuzzleRef | null {
  const parts = raw.trim().split('.');
  const modeId = parts[0]?.toLowerCase() ?? '';
  if (!ID.test(modeId)) return null;
  if (parts.length === 2) {
    const m = DAY.exec(parts[1].toLowerCase());
    const day = m ? Number(m[1]) : NaN;
    return Number.isInteger(day) && day >= 1 ? { modeId, kind: 'daily', day } : null;
  }
  if (parts.length === 3) {
    const difficultyId = parts[1].toLowerCase();
    const salt = parts[2];
    if (!ID.test(difficultyId) || !SALT.test(salt)) return null;
    return { modeId, kind: 'endless', difficultyId, salt };
  }
  return null;
}

/** The hash fragment (without '#') for a challenge link. */
export function challengeHash(link: ChallengeLink): string {
  const params = new URLSearchParams();
  params.set('play', formatPuzzleRef(link.ref));
  if (typeof link.beat === 'number' && Number.isFinite(link.beat) && link.beat > 0) {
    params.set('beat', String(Math.round(link.beat)));
  }
  const by = link.by?.trim().slice(0, MAX_NAME);
  if (by) params.set('by', by);
  return params.toString();
}

/** Parse a location hash (with or without the leading '#'). Null if it isn't a puzzle link. */
export function parseChallengeHash(hash: string): ChallengeLink | null {
  const body = hash.replace(/^#/, '');
  if (!body.includes('play=')) return null;
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(body);
  } catch {
    return null;
  }
  const ref = parsePuzzleRef(params.get('play') ?? '');
  if (!ref) return null;
  const beatRaw = Number(params.get('beat'));
  const beat = Number.isFinite(beatRaw) && beatRaw > 0 && beatRaw < 1e7 ? Math.round(beatRaw) : undefined;
  // Strip control characters from the sender's name.
  const byRaw = [...(params.get('by') ?? '')].filter((c) => c >= ' ' && c !== '\u007f').join('').trim();
  const by = byRaw ? byRaw.slice(0, MAX_NAME) : undefined;
  return { ref, beat, by };
}
